import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Box,
  Button,
  Container,
  Flex,
  Modal,
  Text,
  Tooltip,
  TextInput,
  MultiSelect,
  Group,
  NumberInput,
  Select,
} from '@mantine/core';
import { MantineReactTable } from 'mantine-react-table';
import { useMediaQuery } from "@mantine/hooks";
import { IconEdit, IconPlus, IconTrash } from '@tabler/icons';

import AppBreadcrumb from '../../../components/AppBreadcrumb';
// import AddBundleForm from '../../../components/AddBundleForm';

import { bundle_api } from '../../../services/bundle.service';
import { stock_apis } from '../../Inventory/stock-transfer/stock-transfer.service';
import { invoice_api } from 'pages/transactions/invoice/invoice.service';
import { counters_api } from '../counter/counter.service';
import { set } from 'lodash';
import { date } from 'joi';
import { showSuccessToast, showErrorToast } from '../../../utilities/Toast';

const BundlesList = () => {
  const [bundles, setBundles] = useState([]);
  const [products, setProducts] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editBundle, setEditBundle] = useState(null);
  let isMobileScreen = useMediaQuery("(max-width: 768px)");

  // form state
  const [bundleName, setBundleName] = useState("");
  const [bundleTitle, setBundleTitle] = useState("");
  const [bundleCount, setBundleCount] = useState(1);
  const [offerPrice, setOfferPrice] = useState("");
  const [image, setImage] = useState(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  // NEW STATES (Added Only UI Logic)
  const [activeProductId, setActiveProductId] = useState(null);
  const [productOfferMap, setProductOfferMap] = useState({});
  const [tempProductOffer, setTempProductOffer] = useState("");
  const [productQtyMap, setProductQtyMap] = useState({});
  const [tempProductQty, setTempProductQty] = useState("");
  const [productVariantMap, setProductVariantMap] = useState({});
  const [tempProductVariant, setTempProductVariant] = useState("");

  //initial load bundles
  useEffect(() => {
    loadBundles();
    loadProducts();
    // console.log(productOfferMap,productQtyMap)
  }, []);

  const loadBundles = async () => {
    const res = await bundle_api.get();
    if (res?.success) {
      setBundles(res.data || []);
    }
  };

  const loadProducts = async () => {
    try {
      const countersRes = await counters_api.get();
      const counters = countersRes?.data || [];
      const saleableCounter = counters.find(c => c.saleable === true);
      
      if (saleableCounter) {
        const res = await invoice_api.get_products(saleableCounter.id);
        if (res?.success) {
          setProducts(res.data || []);
        }
      } else {
        showErrorToast({
          title: "Error",
          message: "There is no saleable counter",
        });
      }
    } catch (err) {
      console.error("Error loading products for bundles:", err);
    }
  };

  // Add Bundle function
  const openAdd = async () => {

    setEditBundle(null);
    setBundleName("");
    setBundleTitle("");
    setBundleCount(1);
    setOfferPrice("");
    setSelectedIds([]);
    setImage(null);
    setActiveProductId(null);
    setProductOfferMap({});
    setTempProductOffer("");
    setProductQtyMap({});
    setTempProductQty(1)
    setModalOpen(true);
  };

  // Edit Bundle Function
  const openEdit = async (bundle) => {

    setEditBundle(bundle);
    setBundleName(bundle.name || "");
    setBundleTitle(bundle.title || "");
    setBundleCount(bundle.bundle_count || 1);
    setOfferPrice(bundle.offer_price || "");
    setImage(bundle.image || null);

    // ✅ Set selected IDs
    const ids = (bundle.products || []).map(p => p.counter_stock_id.toString());
    setSelectedIds(ids);

    // ✅ Offer Map
    const offerMap = {};
    bundle.bundle_items?.forEach(p => {
      offerMap[p.counter_stock_id] = p.product_price;
    });
    setProductOfferMap(offerMap);

    // ✅ Quantity Map
    const qtyMap = {};
    bundle.bundle_items?.forEach(p => {
      qtyMap[p.counter_stock_id] = p.product_quantity;
    });
    setProductQtyMap(qtyMap);

    // ✅ Variant Map
    const variantMap = {};
    bundle.bundle_items?.forEach(p => {
      let val = (p.varient || "1pkt").match(/[\d.]+/);
      console.log("varient", val[0])
      let varient = val[0] || 0;
      variantMap[p.counter_stock_id] = varient;
    });
    setProductVariantMap(variantMap);

    // console.log(productOfferMap,offerMap,qtyMap,productQtyMap)


    setActiveProductId(null);
    // setProductOfferMap({});
    setTempProductOffer("");
    setTempProductQty()
    setModalOpen(true);
  };

  //Delete Bundle Function
  const deleteBundle = async id => {
    await bundle_api.delete(id);
    loadBundles();
  };

  const columns = useMemo(
    () => [
      { header: 'Bundle Name', accessorKey: 'name' },
      { header: 'Bundle Count', accessorKey: 'bundle_count' },
      { header: 'Total Products', accessorKey: 'product_count' },
      {
        header: 'Original Price',
        accessorKey: 'original_price',
        Cell: ({ cell }) => `₹ ${cell.getValue()}`,
      },
      {
        header: 'Offer Price',
        accessorKey: 'offer_price',
        Cell: ({ cell }) => `₹ ${cell.getValue()}`,
      },
    ],
    []
  );

  const filteredProducts = useMemo(() => {
    return products.filter(p =>
      (p.title || "").toLowerCase().includes(search.toLowerCase())
    );
  }, [products, search]);

  //Selected Products For The Bundle
  const selectedProducts = useMemo(() => {
    return selectedIds
      .map(id => products.find(p => p.id.toString() === id))
      .filter(Boolean);
  }, [selectedIds, products]);

  //Sum of Original Price (MRP * Qty * Variant)
  const originalPrice = useMemo(() => {
    return selectedProducts.reduce((sum, p) => {

      const mrp = Number(p.mrp || 0);
      const qty = Number(productQtyMap[p.id] || 1);

      let selectedVariant = Number(
        productVariantMap?.[p.id] || 1
      );

      if (p.varientUnit === "g") {
        selectedVariant = selectedVariant / 1000;
      }

      return sum + (mrp * selectedVariant * qty);

    }, 0).toFixed(2);

  }, [selectedProducts, productQtyMap, productVariantMap]);

  // const originalPrice = useMemo(() => {
  //   return selected.reduce((sum, p) => sum + Number(p.mrp || 0), 0);
  // }, [selected]);

  //Active Product Total Price (MRP * Qty * Variant)
  const activeProductMrpTotal = useMemo(() => {
    if (!activeProductId) return 0;

    const product = selectedProducts.find(
      p => p.id === activeProductId
    );

    if (!product) return 0;

    const qty = Number(productQtyMap[product.id] || 1);
    const mrp = Number(product.mrp || 0);
    let selectedVariant = Number(
      productVariantMap?.[product.id] || 1
    );
    console.log("selectedVariant", selectedVariant, "unit", product.varientUnit);
    if (product.varientUnit === "g") {
      console.log("selectedVariant", selectedVariant, "unit", product.varientUnit);
      selectedVariant = selectedVariant / 1000;
      return (mrp * selectedVariant * qty).toFixed(2);
    }

    return (mrp * selectedVariant * qty).toFixed(2);

  }, [activeProductId, selectedProducts, productQtyMap, productVariantMap]);

  // Sum of Offer Price
  const totalOfferPrice = useMemo(() => {
    return selectedProducts.reduce((sum, p) => {
      const price = Number(productOfferMap[p.id] || 0);
      return sum + price;
    }, 0).toFixed(2);
  }, [selectedProducts, productOfferMap]);

  /*  // const totalOfferPrice = useMemo(() => {
    //   let totalPrice =  Object.values(productOfferMap)
    //     .reduce((sum, val) => sum + Number(val || 0), 0)
    //     .toFixed(2);
    //   return totalPrice;
    // }, [productOfferMap]);*/

  //Data shown in the dropdown
  const dropdownData = filteredProducts.map(p => ({
    value: p.id.toString(),
    label: p.title,
    mrp: p.mrp,
    selling_price: p.selling_price,
    stock_qty: p.stock_qty,
    tax: p.tax,
  }));

  //Merged dropdown data both selected and unselected
  const mergedDropdownData = useMemo(() => {
    const selectedOptions = selectedProducts.map(p => ({
      value: p.id.toString(),
      label: p.title,
      mrp: p.mrp,
      selling_price: p.selling_price,
      stock_qty: p.stock_qty,
      tax: p.tax,
    }));

    const map = new Map();

    [...selectedOptions, ...dropdownData].forEach(item => {
      map.set(item.value, item);
    });

    return Array.from(map.values());
  }, [dropdownData, selectedProducts]);

  // Save Bundle Function API
  const saveBundle = async () => {
    const fd = new FormData();

    if(!bundleName){
      showErrorToast({title:"Error",message:"Please enter bundle name"})
      return;
    }

    fd.append("name", bundleName);
    fd.append("title", bundleTitle || "");
    fd.append("offer_price", totalOfferPrice);
    fd.append("bundle_count", bundleCount);
    fd.append("bundle_products", productOfferMap)
    fd.append("original_price", originalPrice)

    if (image && typeof image !== "string") {
      fd.append("image", image);
    }

    // console.log("selectedProducts",selectedProducts,"productOfferMap",productOfferMap,"productQtyMap",productQtyMap,"productVariantMap",productVariantMap);
    fd.append(
      "products",
      JSON.stringify(
        selectedProducts.map(p => ({
          product_id: p.product,
          counter_stock_id: p.id,
          price: productOfferMap[p.id] !== undefined ? Number(productOfferMap[p.id]) : Number(p.selling_price || 0),
          quantity: productQtyMap[p.id] !== undefined ? Number(productQtyMap[p.id]) : 1,
          mrp: p.mrp,
          variant: p.isVarient ? Number(productVariantMap[p.id]) + p.varientUnit : "1pkt"
        }))
      )
    );

    // console.log("selectedProducts",selectedProducts,"productOfferMap",productOfferMap,"productQtyMap",productQtyMap,"productVariantMap",productVariantMap);
    for (let [key, value] of fd.entries()) {
      console.log(key, value);
    }

    const res = editBundle
      ? await bundle_api.edit(editBundle.id, fd)
      : await bundle_api.add(fd);

    if (res?.success) {
      setModalOpen(false);
      loadBundles();
      showSuccessToast({ title: "Success", message: res.message });
    } else {
      showErrorToast({ title: "Error", message: res.message });
    }
  };

  return (
    <Container fluid>
      {/* HEADER (same like SubCategories) */}
      <Box px={25} py={20}>
        <Flex justify="space-between" align="end">
          <Box>
            <Text size="lg" fw={600}>Bundles</Text>
            <AppBreadcrumb />
          </Box>

          <Button onClick={openAdd}>
            <IconPlus size={18} />
            <Text ml={8}>Add Bundle</Text>
          </Button>
        </Flex>
      </Box>

      {/* TABLE */}
      <Box px={25}>
        <MantineReactTable
          columns={columns}
          data={bundles}
          enableRowActions
          positionActionsColumn="last"
          mantineTableBodyRowProps={({ row }) => {
            const bundle = row.original;

            if (!bundle?.products?.length) return {};

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            let rowColor;

            for (const product of bundle.products) {



              const expDate = new Date(product.batch_exp_date);
              expDate.setHours(0, 0, 0, 0);

              const diffDays = Math.ceil(
                (expDate - today) / (1000 * 60 * 60 * 24)
              );

              // 🔴 If batch expiry is null → treat as expired
              if (!product?.batch_exp_date) {
                rowColor = "#f71d1d";
                // break; // highest priority
              }
              // 🟠 Expiring within 30 days
              else if (diffDays <= 30 && !rowColor) {
                rowColor = "#f4c07c";
              }
            }

            return rowColor ? {
              sx: {
                "& td": {
                  backgroundColor: rowColor,
                },
              },
            }
              : {};
          }}
          renderRowActions={({ row }) => (
            <Flex>
              <Tooltip label="Edit Bundle">
                <ActionIcon color="green" onClick={() => openEdit(row.original)}>
                  <IconEdit size={18} />
                </ActionIcon>
              </Tooltip>

              <Tooltip label="Delete Bundle">
                <ActionIcon
                  color="red"
                  ml={10}
                  onClick={() => deleteBundle(row.original.id)}
                >
                  <IconTrash size={18} />
                </ActionIcon>
              </Tooltip>
            </Flex>
          )}
        />
      </Box>
      {/* Add or Edit Model  */}
      <Modal
        opened={modalOpen}
        onClose={() => { setModalOpen(false); }}
        title={editBundle ? "Edit Bundle" : "Add Bundle"}
        size="800px"
        centered
      >
        <TextInput
          label="Bundle Name"
          value={bundleName}
          onChange={e => setBundleName(e.target.value)}
          mb="sm"
          required
        />

        <TextInput
          label="Bundle Title"
          value={bundleTitle}
          onChange={e => setBundleTitle(e.target.value)}
          mb="sm"
        />

        <TextInput
          label="Bundle Count"
          type="number"
          min={1}
          value={bundleCount}
          onChange={e => setBundleCount(e.target.value)}
          mb="sm"
        />

        <input
          type="file"
          accept="image/*"
          onChange={e => setImage(e.target.files[0])}
        />

        {image && (
          <img
            src={typeof image === "string" ? image : URL.createObjectURL(image)}
            alt="bundle"
            style={{
              marginTop: 10,
              width: 120,
              height: 120,
              objectFit: "cover",
              borderRadius: 8,
              border: "1px solid #ddd",
            }}
          />
        )}

        <MultiSelect
          label="Search Product"
          placeholder="Type product name..."
          searchable
          clearable
          data={mergedDropdownData}
          value={selectedIds}
          searchValue={search}
          onSearchChange={setSearch}
          onChange={(values) => {
            setSelectedIds(values);

            // Remove unselected products from offer & qty maps
            setProductOfferMap(prev => {
              const updated = { ...prev };
              Object.keys(updated).forEach(key => {
                if (!values.includes(key.toString())) {
                  delete updated[key];
                }
              });
              return updated;
            });

            setProductQtyMap(prev => {
              const updated = { ...prev };
              Object.keys(updated).forEach(key => {
                if (!values.includes(key.toString())) {
                  delete updated[key];
                }
              });
              return updated;
            });

            if (values.length > 0) {
              const lastId = values[values.length - 1];
              const product = products.find(p => p.id.toString() === lastId);

              if (product) {
                setActiveProductId(product.id);
                setTempProductOffer(productOfferMap[product.id] || "");
                setTempProductQty(productQtyMap[product.id] || 1);
                setTempProductVariant(productVariantMap[product.id] || "");
              }
            } else {
              setActiveProductId(null);
            }
          }}

          maxDropdownHeight={150}
          nothingFound="No products found"
          mb="sm"
          itemComponent={({ label, mrp, selling_price, stock_qty, ...others }) => (
            <Group {...others} position="apart" noWrap>
              <Text size="sm">{label}</Text>
              <Text size="xs" color="dimmed">
                MRP ₹{mrp} | SP ₹{selling_price} | Qty {stock_qty}
              </Text>
            </Group>
          )}
        />

        <Group spacing="md">
          <Text fw={600}>Original Price: ₹ {originalPrice}</Text>

          {activeProductId && (
            <Text fw={600} color="blue">
              Selected Product MRP Total: ₹ {activeProductMrpTotal}
            </Text>
          )}
        </Group>


        {/* PRODUCT PRICING BOX */}
        {selectedProducts.length > 0 && (
          <Box mt="md" p={10} sx={{ border: "1px solid #eee", borderRadius: 8 }}>

            {/* PRODUCT TABS */}
            <Group mb="xs">
              {selectedProducts.map(p => (
                <Button
                  key={p.id}
                  size="xs"
                  variant={activeProductId === p.id ? "filled" : "light"}
                  color={activeProductId === p.id ? "orange" : "gray"}
                  onClick={() => {
                    setActiveProductId(p.id);
                    setTempProductOffer(productOfferMap[p.id] || "");
                    setTempProductQty(productQtyMap[p.id] || 1);
                    setTempProductVariant(productVariantMap[p.id] || "");
                  }}
                >
                  {p.title}
                </Button>
              ))}
            </Group>

            {/* ACTIVE PRODUCT DETAILS */}
            {(() => {
              const activeProduct = selectedProducts.find(
                p => p.id === activeProductId
              );
              if (!activeProduct) return null;

              console.log("Active Product:", activeProduct);

              return (
                <Group mt={6} align="center">

                  {/* MRP */}
                  <Button size="xs" color="green" variant="filled">
                    MRP ₹ {Number(activeProduct.isVarient ? activeProduct.varientUnit === "g" ? activeProduct.mrp * (tempProductVariant / 1000) : activeProduct.mrp * tempProductVariant : activeProduct.mrp || 0).toFixed(2)}
                  </Button>

                  {/* SP */}
                  <Button size="xs" color="blue" variant="filled">
                    SP ₹ {Number(activeProduct.isVarient ? activeProduct.varientUnit === "g" ? activeProduct.selling_price * (tempProductVariant / 1000) : activeProduct.selling_price * tempProductVariant : activeProduct.selling_price || 0).toFixed(2)}
                  </Button>

                  {/* TAX */}
                  <Button size="xs" color="grape" variant="filled">
                    TAX {activeProduct.tax || 0}%
                  </Button>

                  {/* OFFER INPUT */}
                  <Button
                    size="xs"
                    // color="orange"
                    variant="filled"
                    px={0}
                  >
                    <TextInput
                      value={tempProductOffer}
                      onChange={e => setTempProductOffer(e.target.value)}
                      placeholder="EnterPrice"
                      type="number"
                      size="xs"
                      w={85}
                      styles={{
                        input: {
                          backgroundColor: "white",
                          textAlign: "center",
                          fontWeight: 600
                        }
                      }}
                    />
                  </Button>

                  {/* QUANTITY INPUT */}
                  <Button
                    size="xs"
                    variant="filled"
                    px={0}
                  >
                    <NumberInput
                      value={tempProductQty}
                      onChange={(value) => {
                        setTempProductQty(value)
                        setProductQtyMap(prev => ({
                          ...prev,
                          [activeProduct.id]: Number(value || 1)
                        }));
                      }}
                      placeholder="EnterQty"
                      size="xs"
                      min={1}
                      hideControls={false}
                      w={85}
                      styles={{
                        input: {
                          backgroundColor: "white",
                          textAlign: "center",
                          fontWeight: 600
                        }
                      }}
                    /> Qty
                  </Button>

                  {/* VARIANT SELECT */}
                  {activeProduct.isVarient && (
                    <Button size="xs" variant="filled" px={0}>
                      <Select
                        value={tempProductVariant}
                        onChange={(value) => {
                          setTempProductVariant(value);
                          setProductVariantMap(prev => ({
                            ...prev,
                            [activeProduct.id]: value
                          }));
                        }}
                        placeholder="Select Variant"
                        size="xs"
                        w={80}
                        data={(activeProduct.variants || []).map(v => ({
                          value: v.toString(),
                          label: `${v} ${activeProduct.varientUnit || ''}`
                        }))}
                        styles={{
                          input: {
                            backgroundColor: "white",
                            textAlign: "center",
                            fontWeight: 600,
                          }
                        }}
                      />Variant
                    </Button>
                  )}

                  {/* ENTER BUTTON */}
                  <Button
                    size="xs"
                    color="green"
                    disabled={!tempProductOffer}
                    onClick={() => {
                      setProductOfferMap(prev => ({
                        ...prev,
                        [activeProduct.id]: Number(tempProductOffer || 0)
                      }));

                      setProductQtyMap(prev => ({
                        ...prev,
                        [activeProduct.id]: Number(tempProductQty || 1)
                      }));

                      setTempProductOffer("");
                      setTempProductQty("");
                    }}
                  >
                    Enter
                  </Button>

                  {/* CLEAR BUTTON */}
                  <Button
                    size="xs"
                    color="red"
                    onClick={() => {
                      setProductOfferMap(prev => {
                        const copy = { ...prev };
                        delete copy[activeProduct.id];
                        return copy;
                      });

                      setProductQtyMap(prev => {
                        const copy = { ...prev };
                        delete copy[activeProduct.id];
                        return copy;
                      });

                      setProductVariantMap(prev => {
                        const copy = { ...prev };
                        delete copy[activeProduct.id];
                        return copy;
                      });

                      setTempProductOffer("");
                      setTempProductQty(1);
                      setTempProductVariant("");
                    }}
                  >
                    Clear
                  </Button>
                </Group>
              );
            })()}
          </Box>
        )}

        <TextInput
          label="Offer Price (Total of Product Offers)"
          type="number"
          value={totalOfferPrice || 0}
          mt="sm"
          readOnly
          required
        />

        <Group position="right" mt="md">
          <Button variant="default" onClick={() => { setModalOpen(false); }}>
            Cancel
          </Button>
          <Button color="green" onClick={saveBundle}>
            {editBundle ? "Update Bundle" : "Save Bundle"}
          </Button>
        </Group>
      </Modal>
    </Container>
  );
};

export default BundlesList;
